import { useCallback, useEffect, useState } from 'react';
import {
  SchoolProfileNotFoundError,
  fetchSchoolPublicProfile,
} from '../services/schoolProfileApi';
import { isSchoolProfileCandidate, normalizePublicPath } from '../utils/reservedPaths';

export const PROFILE_STATUS = {
  LOADING: 'loading',
  READY: 'ready',
  NOT_FOUND: 'not-found',
  ERROR: 'error',
};

/**
 * Loads a school public profile for a root-level path.
 * Reserved and malformed paths resolve to `not-found` without a network call.
 */
export const useSchoolProfile = (rawPublicPath) => {
  const publicPath = normalizePublicPath(rawPublicPath);
  const [status, setStatus] = useState(PROFILE_STATUS.LOADING);
  const [profile, setProfile] = useState(null);
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => {
    setStatus(PROFILE_STATUS.LOADING);
    setAttempt((previous) => previous + 1);
  }, []);

  useEffect(() => {
    if (!isSchoolProfileCandidate(publicPath)) {
      setProfile(null);
      setStatus(PROFILE_STATUS.NOT_FOUND);
      return undefined;
    }

    const controller = new AbortController();
    let active = true;

    setStatus(PROFILE_STATUS.LOADING);

    fetchSchoolPublicProfile(publicPath, { signal: controller.signal })
      .then((mappedProfile) => {
        if (!active) return;
        setProfile(mappedProfile);
        setStatus(PROFILE_STATUS.READY);
      })
      .catch((error) => {
        if (!active || controller.signal.aborted) return;

        setProfile(null);
        setStatus(
          error instanceof SchoolProfileNotFoundError
            ? PROFILE_STATUS.NOT_FOUND
            : PROFILE_STATUS.ERROR
        );
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [publicPath, attempt]);

  return {
    publicPath,
    profile,
    status,
    isLoading: status === PROFILE_STATUS.LOADING,
    isNotFound: status === PROFILE_STATUS.NOT_FOUND,
    isError: status === PROFILE_STATUS.ERROR,
    retry,
  };
};

export default useSchoolProfile;
